import { axiosPrivate } from "@/lib/axios/axios-private";
import { apiErrorFromEnvelope } from "@/lib/api/errors";
import type { BackendApiResponse } from "@/types/audit-log-types";
import type {
  AlertChartSlice,
  FrameCommentItem,
  ListVideoAlertsParams,
  VideoAlertItem,
  VideoAlertListData,
  VideoAlertListEnvelope,
  VideoAlertListResult,
  VideoStudySummary,
} from "@/types/video-alert-types";

const REMARK_CHART_COLORS = [
  "#6366f1",
  "#0ea5e9",
  "#14b8a6",
  "#a855f7",
  "#f43f5e",
  "#64748b",
] as const;

const REMARK_COLOR_BY_LABEL: Record<string, string> = {
  "Non-unidirectional flow": "#6366f1",
  "Non Unidirectional": "#6366f1",
  Turbulence: "#0ea5e9",
  Eddies: "#14b8a6",
  "Pipe not visible": "#a855f7",
  "Pipe Not Visible": "#a855f7",
  "Smoke not visible": "#f43f5e",
  "Smoke Not Visible": "#f43f5e",
};

const SEVERITY_COLOR_BY_LABEL: Record<string, string> = {
  Low: "#22c55e",
  Medium: "#f59e0b",
  High: "#ef4444",
};

const STATUS_COLOR_BY_LABEL: Record<string, string> = {
  Approved: "#22c55e",
  Rejected: "#ef4444",
  Pending: "#eab308",
};

export type AlertChartKind = "remark" | "severity" | "status";

function normalizeChartLabel(label: string): string {
  return label.trim().toLowerCase();
}

function colorFromMap(
  label: string,
  map: Record<string, string>,
  fallback: string,
): string {
  const exact = map[label];
  if (exact) return exact;
  const normalized = normalizeChartLabel(label);
  const match = Object.entries(map).find(
    ([key]) => key.toLowerCase() === normalized,
  );
  return match?.[1] ?? fallback;
}

/** Distinct palettes per chart so remark / severity / status do not share the same first color. */
export function getAlertChartColors(
  kind: AlertChartKind = "remark",
  slices: Array<Pick<AlertChartSlice, "label">> = [],
): string[] {
  if (kind === "severity") {
    return slices.map((slice) =>
      colorFromMap(slice.label, SEVERITY_COLOR_BY_LABEL, "#64748b"),
    );
  }
  if (kind === "status") {
    return slices.map((slice) =>
      colorFromMap(slice.label, STATUS_COLOR_BY_LABEL, "#64748b"),
    );
  }

  let fallbackIndex = 0;
  return slices.map((slice) => {
    const mapped = colorFromMap(slice.label, REMARK_COLOR_BY_LABEL, "");
    if (mapped) return mapped;
    const color =
      REMARK_CHART_COLORS[fallbackIndex % REMARK_CHART_COLORS.length];
    fallbackIndex += 1;
    return color;
  });
}

export function parseAlertChartSlices(raw: unknown): AlertChartSlice[] {
  if (raw == null) return [];

  if (Array.isArray(raw)) {
    return raw
      .map((entry) => {
        if (!entry || typeof entry !== "object") return null;
        const record = entry as Record<string, unknown>;
        const label = String(
          record.label ?? record.name ?? record.key ?? record.remark ?? "",
        ).trim();
        const value = Number(record.count ?? record.value ?? 0);
        if (!label || !Number.isFinite(value) || value <= 0) return null;
        return { label, value };
      })
      .filter((slice): slice is AlertChartSlice => slice != null);
  }

  if (typeof raw === "object") {
    return Object.entries(raw as Record<string, unknown>)
      .map(([label, value]) => ({
        label: label.trim(),
        value: Number(value) || 0,
      }))
      .filter((slice) => slice.label && slice.value > 0);
  }

  return [];
}

export function buildAlertChartSlicesFromItems(
  items: VideoAlertItem[],
): {
  remark: AlertChartSlice[];
  severity: AlertChartSlice[];
  status: AlertChartSlice[];
} {
  const remarkCounts = new Map<string, number>();
  const severityCounts = new Map<string, number>();
  const statusCounts = new Map<string, number>();

  for (const item of items) {
    const remark = item.screenshot?.remark?.trim() || "Unknown";
    const severity = item.severity?.trim() || "Unknown";
    const status = formatAlertStatusLabel(item.status);
    remarkCounts.set(remark, (remarkCounts.get(remark) ?? 0) + 1);
    severityCounts.set(severity, (severityCounts.get(severity) ?? 0) + 1);
    statusCounts.set(status, (statusCounts.get(status) ?? 0) + 1);
  }

  const toSlices = (counts: Map<string, number>) =>
    [...counts.entries()].map(([label, value]) => ({ label, value }));

  return {
    remark: toSlices(remarkCounts),
    severity: toSlices(severityCounts),
    status: padAlertStatusSlices(toSlices(statusCounts)),
  };
}

const ALERT_STATUS_LABELS = ["Approved", "Rejected", "Pending"] as const;

function formatAlertStatusLabel(status: string | null | undefined): string {
  const normalized = String(status ?? "")
    .trim()
    .toUpperCase();
  if (normalized === "APPROVED") return "Approved";
  if (normalized === "REJECTED") return "Rejected";
  return "Pending";
}

function padAlertStatusSlices(slices: AlertChartSlice[]): AlertChartSlice[] {
  const counts = new Map<string, number>();
  for (const slice of slices) {
    const label = formatAlertStatusLabel(slice.label);
    counts.set(label, (counts.get(label) ?? 0) + slice.value);
  }
  return ALERT_STATUS_LABELS.map((label) => ({
    label,
    value: counts.get(label) ?? 0,
  }));
}

function normalizeVideoAlertItem(item: VideoAlertItem): VideoAlertItem {
  const raw = item as VideoAlertItem & {
    log_id?: string | number | null;
    confidence_percentage?: number | string | null;
    confidenceScore?: number | string | null;
    confidence_score?: number | string | null;
    comment_count?: number | string | null;
    commentsCount?: number | string | null;
  };
  const screenshotRaw = raw.screenshot as
    | (NonNullable<VideoAlertItem["screenshot"]> & {
        confidence_percentage?: number | string | null;
      })
    | null
    | undefined;

  const screenshotPct =
    screenshotRaw?.confidencePercentage ??
    screenshotRaw?.confidence_percentage ??
    null;
  const alertPct =
    raw.confidencePercentage ?? raw.confidence_percentage ?? null;
  const score = raw.confidenceScore ?? raw.confidence_score ?? null;
  const confidencePercentage = alertPct ?? screenshotPct ?? score ?? null;
  const rawCommentCount =
    raw.commentCount ?? raw.comment_count ?? raw.commentsCount ?? null;
  const commentCount =
    rawCommentCount == null || rawCommentCount === ""
      ? undefined
      : Number(rawCommentCount);

  return {
    ...raw,
    logId: raw.logId ?? raw.log_id ?? null,
    confidencePercentage,
    commentCount: Number.isFinite(commentCount) ? commentCount : undefined,
    screenshot: raw.screenshot
      ? {
          ...raw.screenshot,
          confidencePercentage: screenshotPct ?? alertPct ?? score ?? null,
        }
      : raw.screenshot,
  };
}

function parseStudySummary(raw: unknown): VideoStudySummary | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const record = raw as Record<string, unknown>;
  const asText = (value: unknown) => {
    if (value == null) return null;
    const text = String(value).trim();
    return text || null;
  };
  const asNumber = (value: unknown) => {
    if (value == null || value === "") return null;
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  };
  return {
    videoName: asText(record.videoName ?? record.video_name),
    equipmentName: asText(record.equipmentName ?? record.equipment_name),
    interventionName: asText(
      record.interventionName ?? record.intervention_name,
    ),
    recordedAt: (() => {
      const raw = record.recordedAt ?? record.recorded_at;
      if (raw == null || raw === "") return null;
      if (typeof raw === "number" || typeof raw === "string") return raw;
      return null;
    })(),
    videoDuration: asNumber(record.videoDuration ?? record.video_duration),
    videoUrl: asText(record.videoUrl ?? record.video_url),
  };
}

function parseAlertPayload(
  payload: VideoAlertListData | undefined,
  fallbackPage: number,
  fallbackPageSize: number,
): VideoAlertListResult {
  if (!payload) {
    return {
      items: [],
      page: fallbackPage,
      pageSize: fallbackPageSize,
      total: 0,
      remark: [],
      severity: [],
      status: [],
      study: null,
    };
  }

  const items = (payload.items ?? []).map(normalizeVideoAlertItem);
  const fromApi = {
    remark: parseAlertChartSlices(payload.remark),
    severity: parseAlertChartSlices(payload.severity),
    status: padAlertStatusSlices(parseAlertChartSlices(payload.status)),
  };
  const fromItems = buildAlertChartSlicesFromItems(items);

  return {
    items,
    page: payload.page ?? fallbackPage,
    pageSize: payload.pageSize ?? fallbackPageSize,
    total: payload.total ?? 0,
    remark: fromApi.remark.length > 0 ? fromApi.remark : fromItems.remark,
    severity: fromApi.severity.length > 0 ? fromApi.severity : fromItems.severity,
    status:
      fromApi.status.some((slice) => slice.value > 0)
        ? fromApi.status
        : fromItems.status,
    study: parseStudySummary(payload.study),
  };
}

function chartSlicesCoverAllAlerts(
  slices: AlertChartSlice[],
  total: number,
): boolean {
  return (
    slices.length > 0 &&
    slices.reduce((sum, slice) => sum + slice.value, 0) === total
  );
}

/** Chart data for all alerts on a video (not limited to the current table page). */
export async function fetchVideoAlertChartData(
  videoId: string,
  params: Pick<ListVideoAlertsParams, "alertType"> = {},
): Promise<{
  remark: AlertChartSlice[];
  severity: AlertChartSlice[];
  status: AlertChartSlice[];
}> {
  const filterParams = {
    ...(params.alertType?.trim() ? { alertType: params.alertType.trim() } : {}),
  };

  const probe = await fetchVideoAlerts(videoId, {
    page: 1,
    pageSize: 1,
    sortBy: "createdAt",
    sortOrder: "desc",
    ...filterParams,
  });

  if (probe.total === 0) {
    return { remark: [], severity: [], status: [] };
  }

  if (
    chartSlicesCoverAllAlerts(probe.remark, probe.total) &&
    chartSlicesCoverAllAlerts(probe.severity, probe.total) &&
    chartSlicesCoverAllAlerts(probe.status, probe.total)
  ) {
    return {
      remark: probe.remark,
      severity: probe.severity,
      status: probe.status,
    };
  }

  const all =
    probe.total <= 1
      ? probe
      : await fetchVideoAlerts(videoId, {
          page: 1,
          pageSize: probe.total,
          sortBy: "createdAt",
          sortOrder: "desc",
          ...filterParams,
        });

  if (
    chartSlicesCoverAllAlerts(all.remark, all.total) &&
    chartSlicesCoverAllAlerts(all.severity, all.total) &&
    chartSlicesCoverAllAlerts(all.status, all.total)
  ) {
    return {
      remark: all.remark,
      severity: all.severity,
      status: all.status,
    };
  }

  return buildAlertChartSlicesFromItems(all.items);
}

export async function fetchVideoAlerts(
  videoId: string,
  params: ListVideoAlertsParams = {},
): Promise<VideoAlertListResult> {
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 20;
  const trimmedId = videoId.trim();

  if (!trimmedId) {
    throw apiErrorFromEnvelope(
      {
        success: false,
        code: 400,
        errorMessage: "A video ID is required to load frames.",
      },
      "alerts",
    );
  }

  const queryParams: Record<string, string | number> = {
    page,
    pageSize,
    sortBy: params.sortBy ?? "createdAt",
    sortOrder: params.sortOrder ?? "asc",
  };

  if (params.alertType?.trim()) {
    queryParams.alertType = params.alertType.trim();
  }
  if (params.status?.trim()) {
    queryParams.status = params.status.trim().toUpperCase();
  }

  const { data: envelope } = await axiosPrivate.get<VideoAlertListEnvelope>(
    `/api/history/${encodeURIComponent(trimmedId)}/alerts`,
    {
      params: queryParams,
    },
  );

  if (!envelope.success || envelope.code !== 200) {
    throw apiErrorFromEnvelope(envelope, "alerts");
  }

  return parseAlertPayload(envelope.data, page, pageSize);
}

export function getVideoAlertsTotalPages(result: VideoAlertListResult): number {
  if (result.total > 0 && result.pageSize > 0) {
    return Math.max(1, Math.ceil(result.total / result.pageSize));
  }

  return Math.max(1, result.page);
}

export async function updateVideoAlertStatus(
  alertId: string,
  status: "APPROVED" | "REJECTED",
  observationNote: string,
  isScreenshot?: boolean,
): Promise<void> {
  const trimmedId = alertId.trim();
  const trimmedNote = observationNote.trim();
  if (!trimmedId) {
    throw apiErrorFromEnvelope(
      {
        success: false,
        code: 400,
        errorMessage: "A valid frame ID is required.",
      },
      "alerts",
    );
  }
  if (status === "REJECTED" && !trimmedNote) {
    throw apiErrorFromEnvelope(
      {
        success: false,
        code: 400,
        errorMessage: "Observation note is required.",
      },
      "alerts",
    );
  }

  const { data: envelope } = await axiosPrivate.patch<
    BackendApiResponse<unknown>
  >("/api/history/alert/" + trimmedId, {
    status,
    action: status === "APPROVED" ? "APPROVE" : "REJECT",
    ...(trimmedNote ? { comment: trimmedNote } : {}),
    ...(isScreenshot ? { screenshot: true } : {}),
  });

  if (!envelope.success || envelope.code !== 200) {
    throw apiErrorFromEnvelope(envelope, "alerts");
  }
}

export async function fetchFrameComments(videoId: string, frameId: string) {
  const { data: envelope } = await axiosPrivate.get<
    BackendApiResponse<{ items: FrameCommentItem[]; total: number }>
  >(`/api/videos/${videoId}/frames/${frameId}/comments`);
  if (!envelope.success || envelope.code !== 200 || !envelope.data) {
    throw apiErrorFromEnvelope(envelope, "alerts");
  }
  return (envelope.data.items ?? []).map((item) => {
    const raw = item as FrameCommentItem & {
      comment_type?: string | null;
      type?: string | null;
    };
    const normalized = String(
      raw.commentType ?? raw.comment_type ?? raw.type ?? "USER_COMMENT",
    )
      .trim()
      .toUpperCase();
    return {
      ...raw,
      commentType:
        normalized === "REJECTION_REASON" ? "REJECTION_REASON" : "USER_COMMENT",
    } as FrameCommentItem;
  });
}

export async function createFrameComment(
  videoId: string,
  frameId: string,
  comment: string,
  commentType: FrameCommentItem["commentType"] = "USER_COMMENT",
) {
  const { data: envelope } = await axiosPrivate.post<
    BackendApiResponse<FrameCommentItem>
  >(`/api/videos/${videoId}/frames/${frameId}/comments`, {
    comment,
    commentType: commentType ?? "USER_COMMENT",
  });
  // API returns 201 Created for new comments
  if (!envelope.success || (envelope.code !== 200 && envelope.code !== 201) || !envelope.data) {
    throw apiErrorFromEnvelope(envelope, "alerts");
  }
  return envelope.data;
}

export async function updateFrameComment(commentId: string, comment: string) {
  const { data: envelope } = await axiosPrivate.put<BackendApiResponse<FrameCommentItem>>(
    `/api/comments/${commentId}`,
    { comment },
  );
  if (!envelope.success || envelope.code !== 200 || !envelope.data) {
    throw apiErrorFromEnvelope(envelope, "alerts");
  }
  return envelope.data;
}

export async function deleteFrameComment(commentId: string) {
  const { data: envelope } = await axiosPrivate.delete<
    BackendApiResponse<{ success: boolean }>
  >(`/api/comments/${commentId}`);
  if (!envelope.success || envelope.code !== 200) {
    throw apiErrorFromEnvelope(envelope, "alerts");
  }
}
